import React, { useState } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { Flame, EnvelopeSimple, Lock, ArrowRight, Warning } from "@phosphor-icons/react";
import { useAuth, formatAuthError } from "../lib/AuthContext";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const next = new URLSearchParams(location.search).get("next") || "/";

  // Already logged in? bounce to next/dashboard
  if (user && user !== false) {
    return <Navigate to={next} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate(next, { replace: true });
    } catch (err) {
      setError(formatAuthError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell title="Log in" subtitle="Resume tracking the fires that matter to you.">
      <form onSubmit={onSubmit} data-testid="login-form" className="space-y-4">
        <Field
          icon={<EnvelopeSimple size={14} weight="bold" />}
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          testId="login-email"
          required
        />
        <Field
          icon={<Lock size={14} weight="bold" />}
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          testId="login-password"
          required
        />
        {error && (
          <div data-testid="login-error" className="flex items-start gap-2 text-xs text-tac-danger border border-tac-danger/40 bg-tac-danger/10 rounded-sm px-3 py-2">
            <Warning size={14} weight="fill" className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        <button
          type="submit"
          disabled={submitting}
          data-testid="login-submit"
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-tac-primary hover:bg-tac-primary/90 disabled:opacity-50 text-white font-bold uppercase tracking-wider text-xs rounded-sm transition-colors active:scale-[0.99]"
        >
          {submitting ? "Signing in…" : "Log in"}
          {!submitting && <ArrowRight size={14} weight="bold" />}
        </button>
        <div className="text-center pt-1">
          <Link
            to="/forgot-password"
            data-testid="login-forgot-pw-link"
            className="text-[14px] text-tac-dim hover:text-tac-primary transition-colors"
          >
            Forgot your password?
          </Link>
        </div>
      </form>
      <GoogleSignInButton />
      <div className="text-[15px] text-tac-dim text-center mt-6">
        New here?{" "}
        <Link
          to={`/signup${next !== "/" ? `?next=${encodeURIComponent(next)}` : ""}`}
          className="text-tac-primary hover:underline font-bold"
          data-testid="login-to-signup-link"
        >
          Create an account
        </Link>
      </div>
    </AuthShell>
  );
}

/* ---------- Shared UI helpers (also used by SignupPage) ---------- */

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export function GoogleSignInButton() {
  const onGoogle = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div className="mt-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 h-px bg-tac-border" />
        <span className="text-[10px] uppercase tracking-[0.2em] text-tac-faint font-bold">or</span>
        <div className="flex-1 h-px bg-tac-border" />
      </div>
      <button
        type="button"
        data-testid="google-signin-button"
        onClick={onGoogle}
        className="w-full flex items-center justify-center gap-2.5 px-4 py-3 bg-white hover:bg-gray-100 text-gray-800 font-bold uppercase tracking-wider text-xs rounded-sm transition-colors active:scale-[0.99]"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#4285F4" d="M23.52 12.27c0-.85-.08-1.67-.22-2.45H12v4.63h6.46a5.52 5.52 0 0 1-2.4 3.62v3h3.88c2.27-2.09 3.58-5.17 3.58-8.8z" />
          <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.88-3c-1.07.72-2.45 1.15-4.06 1.15-3.13 0-5.78-2.11-6.72-4.95H1.27v3.1A12 12 0 0 0 12 24z" />
          <path fill="#FBBC05" d="M5.28 14.29A7.21 7.21 0 0 1 4.9 12c0-.8.14-1.57.38-2.29V6.6H1.27A12 12 0 0 0 0 12c0 1.94.46 3.77 1.27 5.4l4.01-3.11z" />
          <path fill="#EA4335" d="M12 4.77c1.76 0 3.34.61 4.59 1.8l3.44-3.44A11.97 11.97 0 0 0 12 0 12 12 0 0 0 1.27 6.6l4.01 3.11C6.22 6.88 8.87 4.77 12 4.77z" />
        </svg>
        Continue with Google
      </button>
    </div>
  );
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-[calc(100vh-3.5rem)] bg-tac-bg flex items-start sm:items-center justify-center px-4 py-8 sm:py-12">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 mb-3 rounded-full overflow-hidden bg-tac-surface border border-tac-border flex items-center justify-center">
            <img src="/brand-logo.png" alt="" className="w-12 h-12 object-contain" />
          </div>
          <h1 className="font-display font-black text-2xl text-tac-text text-center">{title}</h1>
          {subtitle && (
            <p className="text-[15px] text-tac-dim text-center mt-1 max-w-xs">{subtitle}</p>
          )}
        </div>
        <div className="bg-tac-surface/60 border border-tac-border rounded-sm p-5 sm:p-6">
          {children}
        </div>
        <p className="text-[15px] uppercase tracking-[0.15em] text-tac-faint text-center mt-5 font-mono-tac">
          <Flame size={9} weight="fill" className="inline -mt-0.5 mr-1 text-tac-primary" />
          Okanogan Highlands Firewatch
        </p>
      </div>
    </div>
  );
}

export function Field({ icon, label, type, value, onChange, autoComplete, required, testId, minLength, helper }) {
  return (
    <label className="block">
      <span className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint mb-1.5 flex items-center gap-1.5">
        {icon}
        {label}
      </span>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        required={required}
        minLength={minLength}
        className="w-full bg-tac-bg border border-tac-border focus:border-tac-primary focus:outline-none rounded-sm px-3 py-2.5 text-[15px] text-tac-text placeholder:text-tac-faint transition-colors"
      />
      {helper && <span className="text-[15px] text-tac-faint mt-1 block">{helper}</span>}
    </label>
  );
}
